/**
 * 3 Nov 2026 (D-1103-NEXT) — "Do next": Today's one list, in order of importance. It merges the lines Today already
 * receives (Needs your attention from `/api/house-today`, Might slip from `/api/might-slip`) — no new read — and puts
 * them in five tiers:
 *
 *   1  Safety          incidents and unit-condition reports
 *   2  Now             for today or already late: pending close-outs, visits not confirmed today, holds, a visit still open
 *   3  Next 48 hours   visits not confirmed tomorrow, a request soon with no Ready volunteer, an outing tomorrow
 *   4  This week       events short of volunteers or places, agreements opened and not signed, needs a new day
 *   5  Other           everything else, in the order Today already gives it
 *
 * Inside a tier the source's own order is kept (Needs your attention first, then Might slip). A kind this file does not
 * know goes to tier 5, never dropped. An item that both sources report (the same visit, request or agreement) is listed
 * once. Pure and deterministic: the same input always gives the same order. Nothing here acts; every entry carries the
 * address its source already used.
 *
 * Admin → Settings "Show Do next on Today" (`HK_TODAY_DO_NEXT`, default Yes). The key starts with HK_ so the page reads
 * it with the day plan settings (`/api/dayplan`), as it reads `HK_TODAY_LIVE_REFRESH`.
 */
import { HOUSE_ATTN_KINDS, groupEntries, HOUSE_PROGRAMS, type HouseAttnEntry, type HouseAttnLine } from './house-today';
import { MIGHT_SLIP_KINDS, type MightSlipLine } from './might-slip';

export const DO_NEXT_SETTING = 'HK_TODAY_DO_NEXT';
/** On unless the row says No. */
export function doNextOn(settings: Record<string, string> | null | undefined): boolean {
  return (settings?.[DO_NEXT_SETTING] ?? 'Yes').trim().toLowerCase() !== 'no';
}

/** Entries drawn before "Show all". */
export const DO_NEXT_SHOWN = 7;

export type DoNextTier = 1 | 2 | 3 | 4 | 5;
export const DO_NEXT_TIERS: readonly DoNextTier[] = [1, 2, 3, 4, 5];
/** The tier's word in the page (i18n `doNext1103.tier.<n>`): text, so no meaning rests on colour. */
export const DO_NEXT_TIER_KEY: Record<DoNextTier, string> = { 1: 'safety', 2: 'now', 3: 'soon', 4: 'week', 5: 'other' };

/** What a kind is worth when its day does not change it: a number is the tier; 'day' means "by the day it is about". */
type Rule = DoNextTier | 'day';

/** Needs your attention: every kind, once. */
export const ATTN_TIER: Record<(typeof HOUSE_ATTN_KINDS)[number], Rule> = {
  incident: 1,
  pendingCloseout: 2,
  hold: 2,
  readingCloseout: 2,
  activityCloseout: 2,
  completionsPending: 2,
  reachOut: 'day',
  outingTomorrow: 3,
  volunteerUnconfirmed: 3,
  noVolunteer: 3,
  techNew: 3,
  complaintFollowUp: 3,
  needsNewDay: 4,
  shortVolunteers: 4,
  agreementOpened: 4,
  untaken: 4,
  outingNotSent: 4,
  claimed: 4,
  needsOk: 4,
  answers: 4,
  activityHelp: 4,
  more: 5,
  workshopProposal: 5,
  visionsReady: 5,
  visionsReferral: 5,
  newVolunteer: 5,
  volunteerNotice: 5,
  readingNote: 5,
  inbox: 5,
};

/** Might slip: every kind, once. */
export const SLIP_TIER: Record<(typeof MIGHT_SLIP_KINDS)[number], Rule> = {
  unconfirmedVisit: 'day',
  noVolunteer: 'day',
  eventBelowTarget: 4,
  agreementWaiting: 4,
  checkIn: 5,
};

/** A "more" item that is already late (a visit from an earlier day still open) is Now; running out of service days is This week. */
const MORE_WHAT_TIER: Record<string, DoNextTier> = { pastDay: 2, completionReturned: 2, slotCliff: 4, notHome: 4, proposal: 4 };

export interface DoNextEntry {
  /** Unique on the page. */
  id: string;
  source: 'attention' | 'slip';
  kind: string;
  tier: DoNextTier;
  /** Attention: the entry (line + item or the line as a count). */
  attn: HouseAttnEntry | null;
  slip: MightSlipLine | null;
  /** The one address, or null where the role cannot open it. */
  href: string | null;
  openIn: 'housekeeping' | 'companion' | 'reading' | null;
  /** How many things it stands for (a count-only line is its count). */
  weight: number;
}

const addDays = (key: string, n: number): string => {
  const y = Number(key.slice(0, 4));
  const m = Number(key.slice(4, 6));
  const d = Number(key.slice(6, 8));
  const dt = new Date(Date.UTC(y, m - 1, d + n));
  return `${dt.getUTCFullYear()}${String(dt.getUTCMonth() + 1).padStart(2, '0')}${String(dt.getUTCDate()).padStart(2, '0')}`;
};

/** By the day: today or earlier is Now; tomorrow or the day after is Next 48 hours; later is This week; no day is Now. */
function byDay(dayKey: string | null, todayKey: string, soon: DoNextTier, later: DoNextTier): DoNextTier {
  if (!dayKey || dayKey <= todayKey) return 2;
  return dayKey <= addDays(todayKey, 1) ? soon : dayKey <= addDays(todayKey, 2) ? 3 : later;
}

export function attnTier(kind: string, entry: HouseAttnEntry, todayKey: string): DoNextTier {
  const rule = (ATTN_TIER as Record<string, Rule | undefined>)[kind];
  if (rule === undefined) return 5;
  if (kind === 'more') return (entry.item?.what && MORE_WHAT_TIER[entry.item.what]) || 5;
  if (rule === 'day') return byDay(entry.item?.dayKey ?? null, todayKey, 3, 3);
  return rule;
}

export function slipTier(l: MightSlipLine, todayKey: string): DoNextTier {
  const rule = (SLIP_TIER as Record<string, Rule | undefined>)[l.kind];
  if (rule === undefined) return 5;
  if (rule === 'day') return l.kind === 'unconfirmedVisit' ? byDay(l.dayKey, todayKey, 3, 3) : byDay(l.dayKey, todayKey, 3, 4);
  return rule;
}

/** The id's last part after the first colon: `reachOut:A1` → `A1`. */
const refOf = (id: string): string => id.slice(id.indexOf(':') + 1);

/**
 * The merged list. `attention` is the page's lines (any order — the program order is applied here, as the grouped view
 * does), `slip` the Might slip lines (empty for a role that does not get them).
 */
export function buildDoNext(attention: readonly HouseAttnLine[], slip: readonly MightSlipLine[], todayKey: string): DoNextEntry[] {
  const raw: DoNextEntry[] = [];
  // the lines in the program order, each program's entries in the order Today's groups give them
  for (const program of HOUSE_PROGRAMS) {
    for (const e of groupEntries(attention, program).entries) {
      const href = e.item ? e.item.href : e.line.href;
      const openIn = e.item ? e.item.openIn : e.line.openIn;
      raw.push({ id: `a:${e.key}`, source: 'attention', kind: e.line.kind, tier: attnTier(e.line.kind, e, todayKey), attn: e, slip: null, href, openIn, weight: e.item ? 1 : Math.max(e.line.count, 1) });
    }
  }
  // a line whose program is not one we know is still shown (never dropped)
  const known = new Set<string>(HOUSE_PROGRAMS);
  for (const l of attention) {
    if (known.has(l.program)) continue;
    const e: HouseAttnEntry = { key: l.id, line: l, item: null, dueKey: null };
    raw.push({ id: `a:${l.id}`, source: 'attention', kind: l.kind, tier: attnTier(l.kind, e, todayKey), attn: e, slip: null, href: l.href, openIn: l.openIn, weight: Math.max(l.count, 1) });
  }
  // the same visit, request or agreement reported by both sources is listed once (the attention entry is kept)
  const seenRef = new Set<string>();
  const seenHref = new Set<string>();
  for (const r of raw) {
    if (r.attn?.item) seenRef.add(`${r.kind === 'agreementOpened' ? 'agreement' : r.kind === 'reachOut' ? 'visit' : r.kind}:${refOf(r.attn.item.id)}`);
    if (r.attn?.item?.href && r.kind === 'noVolunteer') seenHref.add(r.attn.item.href);
  }
  for (const l of slip) {
    const ref = refOf(l.id);
    const dup =
      (l.kind === 'unconfirmedVisit' && seenRef.has(`visit:${ref}`)) ||
      (l.kind === 'agreementWaiting' && l.standing === 'opened' && seenRef.has(`agreement:${ref}`)) ||
      (l.kind === 'noVolunteer' && seenHref.has(l.href));
    if (dup) continue;
    raw.push({ id: `s:${l.id}`, source: 'slip', kind: l.kind, tier: slipTier(l, todayKey), attn: null, slip: l, href: l.href, openIn: l.kind === 'unconfirmedVisit' ? 'housekeeping' : l.kind === 'noVolunteer' ? 'companion' : null, weight: 1 });
  }
  // a stable sort by tier: inside a tier, the order they came in
  return raw.map((r, n) => ({ r, n })).sort((a, b) => a.r.tier - b.r.tier || a.n - b.n).map((x) => x.r);
}

/** What the heading counts: the things the entries stand for. */
export const doNextCount = (entries: readonly DoNextEntry[]): number => entries.reduce((n, e) => n + e.weight, 0);
